const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Service for managing pending actions across authentication flows
 */
const pendingActionService = {
  /**
   * Create a pending action before login redirect
   * @param {string} actionType - Type of action (e.g., 'accept_invite')
   * @param {object} payload - Action-specific data
   * @returns {Promise<string>} - The pending action token
   */
  async createAction(actionType, payload) {
    const response = await fetch(`${API_URL}/pending-action`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ actionType, payload })
    });

    if (!response.ok) {
      throw new Error('Failed to create pending action');
    }

    const data = await response.json();
    
    // Store the token in localStorage (survives page reloads and OAuth redirects)
    localStorage.setItem('pendingActionToken', data.pendingActionToken);
    
    console.log('[PendingAction] Created action token:', data.pendingActionToken.substring(0, 8) + '...');
    
    return data.pendingActionToken;
  },

  /**
   * Check if there's a pending action to execute
   * @returns {string|null} - The pending action token or null
   */
  getPendingToken() {
    return localStorage.getItem('pendingActionToken');
  },

  /**
   * Clear the pending action token
   */
  clearPendingToken() {
    localStorage.removeItem('pendingActionToken');
    // Also clear the old sessionStorage tokens for cleanup
    sessionStorage.removeItem('pendingInviteToken');
    sessionStorage.removeItem('autoAcceptInvite');
  },

  /**
   * Execute a pending action after login
   * @param {string} token - The pending action token
   * @returns {Promise<object>} - The action result including redirect URL
   */
  async executeAction(token) {
    const authToken = localStorage.getItem('token');
    
    if (!authToken) {
      throw new Error('Not authenticated');
    }

    console.log('[PendingAction] Executing action:', token.substring(0, 8) + '...');

    const response = await fetch(`${API_URL}/pending-action/${token}/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to execute pending action');
    }

    const data = await response.json();
    
    // Clear the token after successful execution
    this.clearPendingToken();
    
    console.log('[PendingAction] Action executed successfully:', data);
    
    return data;
  },

  /**
   * Check for and execute any pending action after login
   * Returns the redirect URL if an action was executed, null otherwise
   * @returns {Promise<string|null>}
   */
  async checkAndExecutePendingAction() {
    const token = this.getPendingToken();
    
    if (!token) {
      return null;
    }

    try {
      const result = await this.executeAction(token);
      return result.result?.redirectUrl || null;
    } catch (error) {
      console.error('[PendingAction] Error executing pending action:', error);
      // Clear the token even on error to prevent infinite retries
      this.clearPendingToken();
      return null;
    }
  }
};

export default pendingActionService;
